import React from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { setSidebarCollapsed } from '../actions/sidebar';
import media from '../util/media';

const HEADING_COLOR = '#111';
const MUTED = '#6b7280';
const BORDER = 'rgba(0,0,0,0.08)';

const OuterContainer = styled.div`
  display: grid;
  grid-template-columns: [content] minmax(0, 1fr) [aside] 400px;
  gap: 3rem;
  align-items: start;
  max-width: 1180px;
  margin: 0 auto;
  padding: 150px 50px 100px;

  ${media.tablet`
    grid-template-columns: [content] minmax(0, 1fr) [aside] 320px;
    gap: 2rem;
    padding: 150px 32px 80px;
  `}

  ${media.phone`
    display: block;
    padding: 100px 1.25rem 4rem;
  `}
`;

const Content = styled.div`
  grid-column: content;
  max-width: 70ch;
  font-size: 18px;
  line-height: 1.6;
`;

const Headline = styled.h1`
  margin: 0 0 1.5rem 0;
  color: ${HEADING_COLOR};

  ${media.phone`
    font-size: 34px;
  `}
`;

const Intro = styled.section`
  margin-bottom: 2rem;

  p {
    margin: 0 0 1rem 0;
  }

  p:first-child {
    font-weight: 600;
  }
`;

const ScarcityNote = styled.p`
  margin: 0 0 0.5rem 0;
  font-size: 16px;
  color: ${MUTED};
`;

const SectionHeading = styled.h2`
  margin: 3rem 0 1.5rem 0;
  color: ${HEADING_COLOR};
`;

const Mode = styled.section`
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid ${BORDER};

  &.first {
    margin-top: 0;
    padding-top: 0;
    border-top: none;
  }

  p {
    margin: 0 0 0.75rem 0;
    font-size: 16.5px;
  }

  p:last-child {
    margin-bottom: 0;
  }
`;

const ModeTitle = styled.h3`
  margin: 0 0 0.5rem 0;
  color: ${HEADING_COLOR};
`;

const ProofNote = styled.p`
  margin: 2.5rem 0 0 0;
  padding-top: 1.5rem;
  border-top: 1px solid ${BORDER};
  font-size: 15px;
  color: ${MUTED};

  a {
    color: ${HEADING_COLOR};
  }
`;

/* Sticky right-rail card — the productised, fixed-scope entry point. */
const Aside = styled.aside`
  grid-column: aside;
  position: sticky;
  top: 40px; /* adjust to clear any fixed header */

  ${media.phone`
    position: static;
    margin-top: 3rem;
  `}
`;

const AssessmentCard = styled.section`
  padding: 2rem 2rem 2.25rem;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04), 0 10px 28px rgba(0, 0, 0, 0.06);
`;

const AssessmentLabel = styled.h2`
  margin: 0 0 0.75rem 0;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${MUTED};
`;

const AssessmentLead = styled.p`
  margin: 0 0 1rem 0;
  font-size: 22px;
  font-weight: 600;
  line-height: 1.25;
  color: ${HEADING_COLOR};
`;

const AssessmentBody = styled.p`
  margin: 0 0 1.25rem 0;
  font-size: 16px;
  line-height: 1.55;
`;

const DeliverablesTitle = styled.p`
  margin: 0 0 0.5rem 0;
  font-weight: 600;
  color: ${HEADING_COLOR};
`;

const Deliverables = styled.ul`
  margin: 0 0 1.25rem 1.25rem;
  padding: 0;
  list-style: disc outside;

  li {
    margin-bottom: 0.4rem;
    font-size: 16px;
  }
`;

const AssessmentNote = styled.p`
  margin: 0;
  font-size: 15px;
  color: ${MUTED};
`;

const AssessmentCTA = styled.p`
  margin: 1.25rem 0 0 0;
  font-size: 16px;
  font-weight: 600;
  color: ${HEADING_COLOR};

  a {
    color: inherit;
  }
`;

class Collaborate extends React.PureComponent {
  componentDidMount() {
    this.props.dispatch(setSidebarCollapsed(true));
  }

  render() {
    return (
      <OuterContainer>
        <Content className="main-content">
          <Headline>Work with me</Headline>

          <Intro className="intro">
            <p>
              I help companies build durable software and reduce the technical risk
              that slows teams down.
            </p>
            <p>
              Maybe a system has grown slow or fragile. Maybe development has stalled and
              shipping feels harder than it should. Maybe the problem just needs expertise
              you don't currently have in-house.
            </p>
          </Intro>

          <ScarcityNote className="scarcity">
            I take on a limited number of engagements at a time. Reach out to check
            current availability.
          </ScarcityNote>

          <SectionHeading>Ways to work together</SectionHeading>

          <Mode className="mode first">
            <ModeTitle>Architecture &amp; Technical Advisory</ModeTitle>
            <p>Independent technical judgement.</p>
            <p>
              I review designs, challenge assumptions, and identify risks before they become
              expensive mistakes. The result is a more robust system that allows your team to move faster.
            </p>
          </Mode>

          <Mode className="mode">
            <ModeTitle>System Design &amp; Implementation</ModeTitle>
            <p>From start to finish or by jumping in wherever you need help.</p>
            <p>
              I take a system or subsystem from requirement to working software, solo or alongside
              your team. Built to last: maintainable, secure, and operable in production.
            </p>
          </Mode>

          <Mode className="mode">
            <ModeTitle>Embedded Technical Partner</ModeTitle>
            <p>For teams that need a senior engineer or architect on hand.</p>
            <p>
              I work alongside your team, providing value across system design, implementation,
              operations, and delivery.
            </p>
          </Mode>

          <ProofNote className="proof">
            <a href="/experience">See selected work</a>, or ask for case studies and references.
          </ProofNote>
        </Content>

        <Aside>
          <AssessmentCard className="assessment">
            <AssessmentLabel>System &amp; Architecture Assessment</AssessmentLabel>
            <AssessmentLead>Start with an assessment.</AssessmentLead>
            <AssessmentBody>
              A review of your system and its architecture, security posture, and operational practices.
              A defined start, a defined end, and a report you can act on.
            </AssessmentBody>
            <DeliverablesTitle>Deliverables</DeliverablesTitle>
            <Deliverables>
              <li>Written findings and key risks</li>
              <li>Prioritised recommendations</li>
              <li>A review session to discuss next steps</li>
            </Deliverables>
            <AssessmentNote>
              Scoped around what's hurting your team most.
            </AssessmentNote>
            <AssessmentCTA>
              Get in touch at{' '}
              <a href="mailto:contact@robinsiep.com">contact@robinsiep.com</a>.
            </AssessmentCTA>
          </AssessmentCard>
        </Aside>
      </OuterContainer>
    );
  }
}

export default connect()(Collaborate);
